import React, { useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import type { DriveStep } from 'driver.js'

import { CustomerType, useApi, useFiltersStore, useSearch } from '@/hooks'
import { SkeletonText } from '@/components/atoms'
import {
    Dropdown,
    Logo,
    RadioGroupItem,
    SearchField,
    WidgetSpinner,
} from '@/components/common'
import { useDropdownClickOutside } from '@/components/common/Dropdown/useDropdownClickOutside'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import useLayoutStore from '@/stores/useLayoutStore/useLayoutStore'
import { FilterButton } from '@/components/common/Filter/Filter/FilterButton'
import { type Option } from '../../common/Form/types'
import { highlightMatchingText } from '@/utils/highlightMatchingText'
import { useAnalyticsFiltersStore } from '@/features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import { VirtualizedList } from '@/components/molecules/VirtualizedList/VirtualizedList'
import {
    useAgentBrokerDriverConfig,
    createAgentBrokerDriverConfig,
} from './useAgentBrokerDriverConfig'
import { WIZARD_STORAGE_KEY } from '@/constants/walkthrough'

const ITEM_HEIGHT = 40
const HEADER_HEIGHT = 36
const DESCRIPTION_HEIGHT = 48
const MAX_DROPDOWN_HEIGHT = 448

type UnifiedItem =
    | { type: 'header'; data: { text: string }; key: string }
    | { type: 'description'; data: { text: string }; key: string }
    | {
          type: 'radio'
          data: {
              value: string
              label: string
              name: string
              disabled?: boolean
          }
          key: string
      }

export const AgentBrokerDropdown: React.FC = () => {
    const { t } = useTranslation('translations')
    const [isOpen, setIsOpen] = React.useState(false)
    const isSidebarCollapsed = useLayoutStore(
        (state) => state.isSidebarCollapsed
    )

    const user = useAuthStore((state) => state.user)
    const setCustomer = useAuthStore((state) => state.setCustomer)
    const { useCustomersQuery, useRecentCustomers } = useApi()
    const {
        data: allCustomers,
        isLoading: customersLoading,
        isError: customersError,
    } = useCustomersQuery(user)
    const {
        data: recentCustomers,
        isLoading: recentsLoading,
        isError: recentsError,
    } = useRecentCustomers()

    const customersListIsLoading = customersLoading || recentsLoading
    const customersListHasError = customersError || recentsError

    const customer = useAuthStore((state) => state.customer)
    const setRootNode = useFiltersStore((state) => state.setRootNode)
    const resetActiveFilters = useAnalyticsFiltersStore(
        (state) => state.resetActiveFilters
    )

    const selectedCustomerId = useMemo(() => {
        return customer?.customerId.toString() ?? 'self'
    }, [customer])

    const options: Option<string>[] = useMemo(() => {
        if (!allCustomers?.length) return []

        const recentCustomerIds = new Set(
            recentCustomers?.map((rc) => rc.customer.customerId) ?? []
        )

        const filteredOptions: Option<string>[] = []

        for (const customer of allCustomers) {
            if (
                customer.type === CustomerType.CUSTOMER &&
                !recentCustomerIds.has(customer.customerId)
            ) {
                filteredOptions.push({
                    value: customer.customerId.toString(),
                    label: customer.customerName,
                })
            }
        }

        return filteredOptions.sort((a, b) => a.label.localeCompare(b.label))
    }, [allCustomers, recentCustomers])

    const userFullName = useMemo(() => {
        if (user?.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`
        }
        return ''
    }, [user])

    const selectedOptionFullName = useMemo(() => {
        if (selectedCustomerId && selectedCustomerId !== 'self') {
            const selected = allCustomers?.find(
                (customer) =>
                    customer.customerId.toString() === selectedCustomerId
            )
            return selected?.customerName ?? <SkeletonText />
        }

        return userFullName || <SkeletonText />
    }, [selectedCustomerId, allCustomers, userFullName])

    const { buttonRef, dropdownRef } = useDropdownClickOutside(() => {
        if (isOpen) {
            setSearchValue('')
            setIsOpen(false)
        }
    })

    const driver = useAgentBrokerDriverConfig()
    const isWizardRunningRef = React.useRef(false)

    const applyDriverConfig = React.useCallback(() => {
        driver.setConfig(createAgentBrokerDriverConfig(t))
    }, [driver, t])

    const setupWizard = React.useCallback(() => {
        if (!buttonRef.current) {
            return
        }

        // Reapply the agent broker driver config
        applyDriverConfig()

        const steps: DriveStep[] = [
            {
                element: buttonRef.current,
                popover: {
                    title: t('agent_wizard.step_1.title'),
                    description: t('agent_wizard.step_1.description'),
                    side: 'bottom',
                    showButtons: ['close'],
                },
            },
        ]

        driver.setSteps(steps)

        const originalMoveNext = driver.moveNext.bind(driver)
        const originalDestroy = driver.destroy.bind(driver)

        driver.moveNext = () => {
            if (isWizardRunningRef.current) {
                localStorage.setItem(WIZARD_STORAGE_KEY, 'true')
                window.dispatchEvent(
                    new CustomEvent('spaceSelectorWizardCompleted')
                )
                isWizardRunningRef.current = false
            }
            originalMoveNext()
        }

        driver.destroy = () => {
            if (isWizardRunningRef.current) {
                localStorage.setItem(WIZARD_STORAGE_KEY, 'true')
                window.dispatchEvent(
                    new CustomEvent('spaceSelectorWizardCompleted')
                )
                isWizardRunningRef.current = false
            }
            originalDestroy()
        }

        originalDestroy()
        requestAnimationFrame(() => {
            driver.drive()
            isWizardRunningRef.current = true
        })
    }, [applyDriverConfig, buttonRef, driver, t])

    useEffect(() => {
        const shouldShowWizard =
            localStorage.getItem(WIZARD_STORAGE_KEY) !== 'true'

        if (shouldShowWizard) {
            setupWizard()
        }

        return () => {
            isWizardRunningRef.current = false
            driver.destroy()
        }
    }, [setupWizard, driver])

    useEffect(() => {
        const handleRestartWizard = () => {
            setupWizard()
        }

        window.addEventListener('restartAgentBrokerWizard', handleRestartWizard)
        return () => {
            window.removeEventListener(
                'restartAgentBrokerWizard',
                handleRestartWizard
            )
        }
    }, [setupWizard])

    const toggle = () => {
        driver.destroy()
        setIsOpen((isOpen) => !isOpen)
    }

    const dropdownProps = {
        isOpen,
        onClick: toggle,
        dropdownRef,
    }

    const logoWrapperClassnames = classnames(
        'transition-all duration-150 easing-in-out',
        'h-logoHeight w-full relative overflow-hidden [&_a]:block',
        {
            'px-3': !isSidebarCollapsed,
        }
    )

    const recentOptions = useMemo<Option<string>[]>(() => {
        const nonCustomerIds =
            allCustomers
                ?.filter((c) => c.type !== CustomerType.CUSTOMER)
                .map((c) => c.customerId) ?? []

        return (
            recentCustomers
                ?.filter(
                    (recentCustomer) =>
                        !nonCustomerIds.includes(
                            recentCustomer.customer.customerId
                        )
                )
                .map((recentCustomer) => ({
                    value: recentCustomer.customer.customerId.toString(),
                    label: recentCustomer.customer.customerName,
                })) ?? []
        )
    }, [recentCustomers, allCustomers])

    const mergedOptions = useMemo(() => {
        const mergedOptions: Option[] = [...options, ...recentOptions]
        return mergedOptions
    }, [options, recentOptions])

    const {
        searchValue,
        setSearchValue,
        options: displayedOptions,
    } = useSearch(() => ({
        options: mergedOptions,
    }))

    const handleRadioChange = useCallback(
        (selectedId: string) => {
            if (selectedId === 'self') {
                setCustomer(null)
                setSearchValue('')
                setIsOpen(false)
            } else {
                const selected = allCustomers?.find(
                    (customer) => customer.customerId.toString() === selectedId
                )
                if (selected) {
                    setRootNode(undefined)
                    resetActiveFilters()
                    setCustomer(selected)
                    setSearchValue('')
                    setIsOpen(false)
                }
            }
        },
        [
            allCustomers,
            resetActiveFilters,
            setCustomer,
            setRootNode,
            setSearchValue,
        ]
    )

    const renderLabel = useCallback(
        (option: Option) => {
            return (
                <>
                    {highlightMatchingText({
                        text: option.label,
                        matchText: searchValue,
                    })}
                </>
            )
        },
        [searchValue]
    )

    const dropdownMenuItems = useMemo<UnifiedItem[]>(() => {
        const items: UnifiedItem[] = [
            {
                type: 'header',
                data: { text: t('sidebar_menu.agent_dropdown.my_space') },
                key: 'my-space-header',
            },
            {
                type: 'radio',
                data: {
                    value: 'self',
                    label: userFullName,
                    name: 'agent-dropdown-my-spaces-radio',
                },
                key: 'self',
            },
        ]

        if (recentOptions.length > 0) {
            items.push({
                type: 'header',
                data: { text: t('sidebar_menu.agent_dropdown.recent_spaces') },
                key: 'recent-spaces-header',
            })
            recentOptions.forEach((option: Option<string>) => {
                items.push({
                    type: 'radio',
                    data: {
                        ...option,
                        name: 'agent-dropdown-recent-spaces-radio',
                    },
                    key: `recent-${String(option.value)}`,
                })
            })
        }

        items.push({
            type: 'header',
            data: { text: t('sidebar_menu.agent_dropdown.other_spaces') },
            key: 'other-spaces-header',
        })
        if (options.length > 0) {
            options.forEach((option) => {
                items.push({
                    type: 'radio',
                    data: {
                        ...option,
                        name: 'agent-dropdown-other-spaces-radio',
                    },
                    key: `other-${String(option.value)}`,
                })
            })
        } else {
            items.push({
                type: 'description',
                data: { text: t('sidebar_menu.agent_dropdown.no_customers') },
                key: 'no-customers',
            })
        }

        return items
    }, [recentOptions, options, userFullName, t])

    const renderUnifiedItem = useCallback(
        (item: UnifiedItem) => {
            if (item.type === 'header') {
                return (
                    <div className="text-heading-caps mt-4 px-4 pb-1 pt-2 [:first-child>&]:mt-0">
                        {item.data.text}
                    </div>
                )
            }

            if (item.type === 'description') {
                return <div className="px-4 text-sm">{item.data.text}</div>
            }

            return (
                <RadioGroupItem
                    key={item.key}
                    name={item.data.name}
                    value={item.data.value}
                    checked={selectedCustomerId === item.data.value}
                    disabled={!!item.data.disabled}
                    onChange={() => {
                        handleRadioChange(item.data.value)
                    }}
                    itemWrapperClassname="px-4 py-2 mt-1"
                    labelClassname="whitespace-nowrap text-ellipsis overflow-hidden"
                >
                    {item.data.label}
                </RadioGroupItem>
            )
        },
        [selectedCustomerId, handleRadioChange]
    )

    const getItemHeight = useCallback((item: UnifiedItem) => {
        if (item.type === 'header') {
            return HEADER_HEIGHT
        }
        if (item.type === 'description') {
            return DESCRIPTION_HEIGHT
        }
        return ITEM_HEIGHT
    }, [])

    return (
        <Dropdown
            name="agent-dropdown"
            buttonProps={{
                name: 'agent-dropdown-button',
                label: '',
                valueLabel: (
                    <div className="space-y-1 pt-1">
                        <div className={logoWrapperClassnames}>
                            <Logo className="absolute inset-0 h-full w-full object-contain object-left" />
                        </div>
                        <div className="relative">
                            &nbsp;
                            <div
                                className="absolute inset-0 overflow-hidden text-ellipsis whitespace-nowrap text-start text-sm text-subtle"
                                data-testid="dropdown-agent-name"
                            >
                                {selectedOptionFullName}
                            </div>
                        </div>
                    </div>
                ),
                buttonRef,
                wrapperClassname: '!items-start pb-1 pr-1 pt-1',
                iconClassname: isSidebarCollapsed ? '!hidden' : undefined,
                className: classnames('w-full relative', {
                    'pr-3': isSidebarCollapsed,
                }),
            }}
            buttonComponent={FilterButton}
            buttonWrapperClassname="w-full"
            horizontalAlign="left"
            dropdownClassname="py-4 !min-w-[auto] z-[50] items-start w-filterContentMinWidth overflow-hidden"
            data-testid="agent-dropdown"
            {...dropdownProps}
        >
            <div className="grid max-h-agentDropdownHeight grid-rows-[auto,1fr] gap-4">
                <div className="px-4">
                    <SearchField
                        name="agent-dropdown-search-field"
                        value={searchValue}
                        onChange={setSearchValue}
                        placeholder={t(
                            'sidebar_menu.agent_dropdown.search_placeholder'
                        )}
                    />
                </div>
                {customersListIsLoading && (
                    <div
                        data-testid="loading-content"
                        className="flex h-filterContentMinHeight place-items-center"
                    >
                        <WidgetSpinner />
                    </div>
                )}
                {customersListHasError && (
                    <div
                        data-testid="error-content"
                        className="flex h-filterContentMinHeight items-center justify-center"
                    >
                        <p className="mx-auto max-w-48 text-center text-sm font-normal leading-6">
                            {t(`sidebar_menu.agent_dropdown.error_message`)}
                        </p>
                    </div>
                )}
                {!customersListIsLoading &&
                    !customersListHasError &&
                    (searchValue.trim().length ? (
                        displayedOptions.length > 0 ? (
                            <div className="space-y-4 overflow-auto">
                                <VirtualizedList
                                    items={displayedOptions}
                                    containerHeight={MAX_DROPDOWN_HEIGHT}
                                    getItemHeight={() => ITEM_HEIGHT}
                                    renderItem={(option) => (
                                        <RadioGroupItem
                                            key={option.value}
                                            name={`agent-dropdown-search-spaces-radio`}
                                            value={option.value}
                                            checked={
                                                selectedCustomerId ===
                                                option.value
                                            }
                                            disabled={!!option.disabled}
                                            onChange={() => {
                                                handleRadioChange(option.value)
                                            }}
                                            itemWrapperClassname="px-4 py-2 mt-1 [&:is(:first-child>*)]:!mt-0"
                                            labelClassname="whitespace-nowrap text-ellipsis overflow-hidden"
                                            rawLabel={option.label}
                                        >
                                            {renderLabel(option)}
                                        </RadioGroupItem>
                                    )}
                                />
                            </div>
                        ) : (
                            <div
                                data-testid="empty-search-content"
                                className="flex h-filterContentMinHeight items-center justify-center px-4"
                            >
                                <p className="mx-auto max-w-48 text-center text-sm font-normal leading-6">
                                    {t(
                                        'sidebar_menu.agent_dropdown.no_search_results'
                                    )}
                                </p>
                            </div>
                        )
                    ) : (
                        <VirtualizedList
                            items={dropdownMenuItems}
                            containerHeight={MAX_DROPDOWN_HEIGHT}
                            renderItem={renderUnifiedItem}
                            getItemHeight={getItemHeight}
                        />
                    ))}
            </div>
        </Dropdown>
    )
}
